// ==================== SENDGRID SERVICE (Frontend) ====================
// Frontend service to call Firebase Functions for sending emails via SendGrid
// This service acts as a bridge between the frontend and backend email operations
// ================================================================

import { logger } from '../utils/logger';
import { fetchWithAppCheck } from '../utils/fetchWithAppCheck';

const FIREBASE_FUNCTIONS_URL = 'https://us-central1-aigicare-prod.cloudfunctions.net';

/**
 * Send email using Firebase Function (which uses SendGrid)
 * @param {Object} options - Email options
 * @param {string|string[]} options.to - Recipient email address(es)
 * @param {string} options.subject - Email subject
 * @param {string} options.html - HTML content of the email
 * @param {string} [options.text] - Plain text content of the email
 * @param {Array} [options.attachments] - Array of attachment objects
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const sendEmail = async ({ to, subject, html, text, attachments }) => {
  try {
    const response = await fetchWithAppCheck(`${FIREBASE_FUNCTIONS_URL}/sendGenericEmail`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to,
        subject,
        html,
        text,
        attachments,
      }),
    });

    const data = await response.json();

    if (response.ok && data.status === 'success') {
      return { success: true };
    } else {
      return {
        success: false,
        error: 'Failed to send email. Please try again later.',
      };
    }
  } catch (error) {
    logger.error('Error sending email:', error);
    return {
      success: false,
      error: 'Failed to send email. Please try again later.',
    };
  }
};

/**
 * Send meeting invitation email
 * @param {string} email - Recipient email address
 * @param {Object} meetingData - Meeting details
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const sendMeetingInvitation = async (email, meetingData) => {
  try {
    const response = await fetchWithAppCheck(`${FIREBASE_FUNCTIONS_URL}/sendMeetingInvitation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        meetingData,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    });

    const data = await response.json();

    if (response.ok && data.status === 'success') {
      return {
        success: true,
        message: data.message || 'Meeting invitation sent successfully',
      };
    } else {
      return {
        success: false,
        message: 'Failed to send meeting invitation. Please try again later.',
      };
    }
  } catch (error) {
    logger.error('Error sending meeting invitation:', error);
    return {
      success: false,
      message: 'Failed to send meeting invitation. Please try again later.',
    };
  }
};

/**
 * Send patient report via email
 * @param {Object} options - Report options
 * @param {string} options.email - Recipient email address
 * @param {string} options.subject - Email subject
 * @param {string} options.pdfData - Base64 encoded PDF data
 * @param {string} options.fileName - PDF file name
 * @param {string} [options.body] - Custom email body HTML
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const sendPatientReport = async ({ email, subject, pdfData, fileName, body }) => {
  try {
    const response = await fetchWithAppCheck(`${FIREBASE_FUNCTIONS_URL}/sendPatientReport`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        subject,
        pdfData,
        fileName,
        body,
      }),
    });

    const data = await response.json();

    if (response.ok && data.status === 'success') {
      return {
        success: true,
        message: data.message || 'Report sent successfully',
      };
    } else {
      return {
        success: false,
        message: 'Failed to send report. Please try again later.',
      };
    }
  } catch (error) {
    logger.error('Error sending patient report:', error);
    return {
      success: false,
      message: 'Failed to send report. Please try again later.',
    };
  }
};

/**
 * Send bulk emails
 * @param {Object} options - Bulk email options
 * @param {string[]} options.emails - Array of recipient email addresses
 * @param {string} options.subject - Email subject
 * @param {string} options.pdfData - Base64 encoded PDF data
 * @param {string} options.fileName - PDF file name
 * @param {string} [options.body] - Custom email body HTML
 * @returns {Promise<{success: boolean, message: string, results?: Array}>}
 */
export const sendBulkEmails = async ({ emails, subject, pdfData, fileName, body }) => {
  try {
    const response = await fetchWithAppCheck(`${FIREBASE_FUNCTIONS_URL}/sendBulkEmails`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        emails,
        subject,
        pdfData,
        fileName,
        body,
      }),
    });

    const data = await response.json();

    if (response.ok && data.status === 'success') {
      return {
        success: true,
        message: data.message || 'Emails sent successfully',
        results: data.results,
      };
    } else {
      return {
        success: false,
        message: 'Failed to send bulk emails. Please try again later.',
        results: data.results,
      };
    }
  } catch (error) {
    logger.error('Error sending bulk emails:', error);
    return {
      success: false,
      message: 'Failed to send bulk emails. Please try again later.',
    };
  }
};

export default {
  sendEmail,
  sendMeetingInvitation,
  sendPatientReport,
  sendBulkEmails,
};
